import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronsLeftRight } from "lucide-react";

/**
 * Draggable Before/After image comparison slider.
 * Mouse + touch + keyboard support. Pass either remote URLs (https://...) or
 * /api/uploads/<id> paths (we prepend REACT_APP_BACKEND_URL automatically).
 */
export default function BeforeAfterSlider({
  beforeImage,
  afterImage,
  alt = "before-after",
  className = "",
  beforeLabel = "Before",
  afterLabel = "After",
}) {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(50); // %
  const [dragging, setDragging] = useState(false);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    setWidth(el.offsetWidth);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const resolve = (u) =>
    u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${u}` : u;

  const updateFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, pct)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClientX(cx);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, updateFromClientX]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPosition((p) => Math.max(0, p - 5));
    if (e.key === "ArrowRight") setPosition((p) => Math.min(100, p + 5));
  };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className={`relative w-full h-full overflow-hidden select-none ${className}`}
      onMouseDown={(e) => { setDragging(true); updateFromClientX(e.clientX); }}
      onTouchStart={(e) => { setDragging(true); updateFromClientX(e.touches[0].clientX); }}
    >
      {/* After image (full) */}
      <img
        src={resolve(afterImage)}
        alt={`${alt} after`}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* Before image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${position}%` }}
      >
        <img
          src={resolve(beforeImage)}
          alt={`${alt} before`}
          draggable={false}
          // Keep the underlying image at full container width so the slice doesn't squash
          style={{ width: width || "100%" }}
          className="h-full object-cover max-w-none"
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 px-3 py-1.5 bg-[#151515]/85 text-[#F5F3EF] text-[10px] uppercase tracking-widest pointer-events-none">
        {beforeLabel}
      </div>
      <div className="absolute top-4 right-4 px-3 py-1.5 bg-[#8C1D30] text-[#F5F3EF] text-[10px] uppercase tracking-widest pointer-events-none">
        {afterLabel}
      </div>

      {/* Drag handle line + knob */}
      <div
        className="absolute top-0 bottom-0 w-px bg-[#F5F3EF] pointer-events-none shadow-[0_0_10px_rgba(0,0,0,0.5)]"
        style={{ left: `${position}%` }}
      />
      <button
        type="button"
        tabIndex={0}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        aria-label="Drag to compare before and after"
        data-testid="before-after-handle"
        onKeyDown={onKey}
        onMouseDown={(e) => { e.stopPropagation(); setDragging(true); }}
        onTouchStart={(e) => { e.stopPropagation(); setDragging(true); }}
        className="absolute top-1/2 w-12 h-12 -ml-6 -mt-6 rounded-full bg-[#F5F3EF] flex items-center justify-center text-[#151515] cursor-ew-resize shadow-xl hover:scale-105 transition-transform"
        style={{ left: `${position}%` }}
      >
        <ChevronsLeftRight className="w-5 h-5" />
      </button>
    </div>
  );
}
